//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { FeatureNames } from '@bvengineering/curalate-features';
import { IStateParamsService, IUrlRouterProvider } from 'angular-ui-router';

import { hasPermissionForTask } from '@services/permissions';

import { crl8 } from 'app/curalate.globals';
import {
  NavGroup,
  StateFactory,
  componentNameToTemplate,
  hasAllFeatures,
  hasFeature,
  hasSomeFeatures,
  lazyLoadModule,
  lazyLoadSubModule,
} from 'app/initialization/routing/stateHelpers';

export function routingRules(urlRouterProvider: IUrlRouterProvider) {
  urlRouterProvider.when(
    '/social/rights-management',
    '/account-settings/rights-requests',
  );
  urlRouterProvider.when('/settings/account', '/account-settings/social-media');
  urlRouterProvider.when(
    '/settings/reports',
    '/account-settings/email-reports',
  );
  urlRouterProvider.when('/settings/users', '/account-settings/users');
  urlRouterProvider.when(
    '/settings/devices',
    '/account-settings/mobile-notifications',
  );
  urlRouterProvider.when(
    '/settings/rights-management',
    '/account-settings/rights-requests',
  );
  urlRouterProvider.when(
    '/settings/tracking-parameters',
    '/account-settings/tracking-parameters',
  );
  urlRouterProvider.when('/catalog', '/account-settings/product-catalog');
  urlRouterProvider.when(
    '/catalog/summary',
    '/account-settings/product-catalog/summary',
  );
  urlRouterProvider.when(
    '/catalog/:catalogId?',
    '/account-settings/product-catalog/:catalogId?',
  );
  urlRouterProvider.when(
    '/integration-settings/ecommerce-setup',
    '/account-settings/ecommerce-setup',
  );
  urlRouterProvider.when('/settings', '/account-settings');
  urlRouterProvider.when('/settings/ingestion', '/settings/tools');
  urlRouterProvider.when(
    '/account-settings/analytics-providers',
    '/account-settings/analytics',
  );
  urlRouterProvider.when(
    '/account-settings/tracking-params',
    '/account-settings/tracking-parameters',
  );

  const isCreatorCampaignsEnabled = hasFeature(FeatureNames.CreatorCampaigns);

  if (!isCreatorCampaignsEnabled) {
    urlRouterProvider.when(
      '/account-settings/email-settings',
      '/account-settings',
    );
    urlRouterProvider.when(
      /\/account-settings\/email-settings\/.*/,
      '/account-settings',
    );
  }
}

const isNotBazaarvoiceSyndicationOnlyClient = () => {
  const isShareV2Client = !!crl8.session?.client.isShareV2Vendor;
  const isBazaarvoiceSyndicationEnabled = hasFeature(
    FeatureNames.BazaarvoiceNetworkSyndicationFrontend,
  );
  return !(isShareV2Client && isBazaarvoiceSyndicationEnabled);
};

export const states = {
  settings: StateFactory({
    url: '/settings',
    template: componentNameToTemplate('settingsPage'),
    redirectTo: 'settings.account',
    data: {
      title: 'Settings',
      navGroup: NavGroup.HOME,
    },
    resolve: {
      ...lazyLoadModule(
        'curalate.account-settings',
        () => import('app/account-settings/SettingsApp'),
      ),
    },
  }),
  'settings.tools': StateFactory({
    url: '/tools',
    views: {
      'settings-main': {
        template: componentNameToTemplate('toolsPage'),
      },
    },
    data: {
      title: 'Apps & Tools',
      navGroup: NavGroup.HOME,
    },
  }),
  'account-settings': StateFactory({
    url: '/account-settings',
    template: componentNameToTemplate('accountSettingsPage'),
    redirectTo: 'account-settings.social',
    data: {
      title: 'Settings',
      navGroup: NavGroup.HOME,
    },
    resolve: {
      ...lazyLoadModule(
        'curalate.account-settings',
        () => import('app/account-settings/SettingsApp'),
      ),
    },
  }),
  'account-settings.social': StateFactory({
    url: '/social-media?oauth-network&oauth-error',
    views: {
      'settings-header': {
        template: `<settings-page-header title="Social Media Accounts"></settings-page-header>`,
      },
      'settings-content': {
        template: componentNameToTemplate('socialMediaAccountsPage'),
      },
    },
    data: {
      title: 'Social Media Accounts',
      navGroup: NavGroup.HOME,
    },
  }),
  'account-settings.analytics': StateFactory({
    url: '/analytics',
    views: {
      'settings-header': {
        template:
          '<settings-page-header title="Analytics Providers"></settings-page-header>',
      },
      'settings-content': {
        template: componentNameToTemplate('analyticsProvidersSettingsPage'),
      },
    },
    data: {
      title: 'Analytics Providers',
      navGroup: NavGroup.HOME,
      hasAccess: () => isNotBazaarvoiceSyndicationOnlyClient(),
    },
  }),
  'account-settings.tracking': StateFactory({
    url: '/tracking-parameters',
    views: {
      'settings-header': {
        template:
          '<settings-page-header title="Tracking Parameters"></settings-page-header>',
      },
      'settings-content': {
        template: componentNameToTemplate('trackingParamsPage'),
      },
    },
    data: {
      title: 'Tracking Parameters',
      navGroup: NavGroup.HOME,
      hasAccess: () => isNotBazaarvoiceSyndicationOnlyClient(),
    },
  }),
  'account-settings.rights': StateFactory({
    url: '/rights-requests',
    views: {
      'settings-header': {
        template: componentNameToTemplate('rightsManagementHeader'),
      },
      'settings-content': {
        template: componentNameToTemplate('rightsManagementPage'),
      },
    },
    data: {
      title: 'Rights Requests',
      navGroup: NavGroup.HOME,
      hasAccess: () => hasAllFeatures(FeatureNames.RightsManagementPage),
    },
  }),
  'account-settings.users': StateFactory({
    url: '/users',
    views: {
      'settings-header': {
        template: componentNameToTemplate('userManagementHeader'),
      },
      'settings-content': {
        template: componentNameToTemplate('teamManagement'),
      },
    },
    data: {
      title: 'Users',
      navGroup: NavGroup.HOME,
      hasAccess: () => hasPermissionForTask('admin:create_or_update_user'),
    },
  }),
  'account-settings.mobile': StateFactory({
    url: '/mobile-notifications',
    views: {
      'settings-header': {
        template:
          '<settings-page-header title="Mobile Notifications"></settings-page-header>',
      },
      'settings-content': {
        template: componentNameToTemplate('deviceSettings'),
      },
    },
    data: {
      title: 'Mobile Notifications',
      navGroup: NavGroup.HOME,
      hasAccess: () => {
        return (
          isNotBazaarvoiceSyndicationOnlyClient() &&
          hasFeature(FeatureNames.SocialDashboards)
        );
      },
    },
  }),
  'account-settings.email': StateFactory({
    url: '/email-reports',
    views: {
      'settings-header': {
        template: componentNameToTemplate('emailReportsHeader'),
      },
      'settings-content': {
        template: componentNameToTemplate('reportSubscriptionsPage'),
      },
    },
    data: {
      title: 'Email Reports',
      navGroup: NavGroup.HOME,
      hasAccess: () => hasAllFeatures('automated_reporting_refresh'),
    },
  }),
  'account-settings.creator-email-settings': StateFactory({
    url: '/email-settings',
    redirectTo: 'account-settings.creator-email-settings.setup',
    views: {
      'settings-header': {
        template: componentNameToTemplate('emailSettingsHeader'),
      },
      'settings-content': {
        template: componentNameToTemplate('emailSettingsPage'),
      },
    },
    data: {
      title: 'Email Settings',
      navGroup: NavGroup.HOME,
    },
  }),
  'account-settings.creator-email-settings.setup': StateFactory({
    url: '/setup',
    views: {
      'email-settings-content': {
        template: componentNameToTemplate('emailSetupSettings'),
      },
    },
    data: {
      title: 'Email Settings',
      navGroup: NavGroup.HOME,
    },
  }),
  'account-settings.creator-email-settings.templates': StateFactory({
    url: '/templates',
    views: {
      'email-settings-content': {
        template: componentNameToTemplate('emailTemplatesSettings'),
      },
    },
    data: {
      title: 'Email Settings',
      navGroup: NavGroup.HOME,
    },
  }),
  'account-settings.creator-email-settings-new': StateFactory({
    url: '/email-settings/create/:emailTemplateId',
    views: {
      'settings-header': {
        template: componentNameToTemplate('emailSettingsHeader'),
      },
      'settings-content': {
        template: componentNameToTemplate('upsertEmailSettingsPage'),
      },
    },
    data: {
      title: 'Email Settings',
      navGroup: NavGroup.HOME,
    },
  }),
  'account-settings.creator-email-settings-edit': StateFactory({
    url: '/email-settings/edit/:emailTemplateId',
    views: {
      'settings-header': {
        template: componentNameToTemplate('emailSettingsHeader'),
      },
      'settings-content': {
        template: componentNameToTemplate('upsertEmailSettingsPage'),
      },
    },
    data: {
      title: 'Email Settings',
      navGroup: NavGroup.HOME,
    },
  }),
  'account-settings.wallets': StateFactory({
    url: '/campaigns-budget',
    views: {
      'settings-header': {
        template:
          '<settings-page-header title="Campaigns Budget"></settings-page-header>',
      },
      'settings-content': {
        template: componentNameToTemplate('walletsPage'),
      },
    },
    data: {
      title: 'Campaigns Budget',
      navGroup: NavGroup.HOME,
      hasAccess: () => {
        return hasAllFeatures(
          FeatureNames.VibePayments,
          FeatureNames.CreatorCampaigns,
        );
      },
    },
  }),
  'account-settings.product': StateFactory({
    url: '/product-catalog',
    redirectTo: 'account-settings.product.summary',
    views: {
      'settings-header': {
        template:
          '<settings-page-header title="Product Catalog"></settings-page-header>',
      },
      'settings-content': {
        template: componentNameToTemplate('catalogPage'),
      },
    },
    data: {
      title: 'Product Catalog',
      navGroup: NavGroup.HOME,
    },
    // custom resolve function put in place to
    // connect the scoped angular app and imports
    resolve: {
      ...lazyLoadSubModule(
        'curalate.catalog',
        () => import('app/catalog/catalog.app'),
      ),
    },
  }),
  'account-settings.product.summary': StateFactory({
    url: '/summary',
    views: {
      'product-feed-health-view': {
        template: '<product-feed-health></product-feed-health>',
      },
    },
    data: {
      title: 'Product Catalog',
      navGroup: NavGroup.HOME,
    },
  }),
  'account-settings.ecommerceSetup': StateFactory({
    url: '/ecommerce-setup',
    views: {
      'settings-header': {
        template:
          '<settings-page-header title="Product Catalog"></settings-page-header>',
      },
      'settings-content': {
        template: componentNameToTemplate('ecommerceIntegrations'),
      },
    },
    data: {
      title: 'Integration Setup',
      navGroup: NavGroup.HOME,
      hasAccess: () =>
        hasSomeFeatures(
          'ecommerce_integrations_magento',
          'ecommerce_integrations_shopify',
        ),
    },
  }),
  'account-settings.product.feed-health-catalog': StateFactory({
    url: '/:catalogId?',
    views: {
      'product-feed-health-view': {
        template:
          '<product-feed-health catalog-id="catalogId"></product-feed-health>',
        controller: [
          '$scope',
          '$stateParams',
          (
            $scope: { catalogId?: string },
            $stateParams: IStateParamsService,
          ) => {
            $scope.catalogId = $stateParams.catalogId as string;
          },
        ],
      },
    },
    data: {
      title: 'Product Catalog',
      navGroup: NavGroup.HOME,
    },
  }),
  'account-settings.account': StateFactory({
    url: '/my-account',
    views: {
      'settings-header': {
        template:
          '<settings-page-header title="My Account"></settings-page-header>',
      },
      'settings-content': {
        template: componentNameToTemplate('myAccountPage'),
      },
    },
    data: {
      title: 'My Account',
      navGroup: NavGroup.HOME,
    },
  }),
  'account-settings.content-curation-guidelines': StateFactory({
    url: '/content-curation-guidelines',
    views: {
      'settings-header': {
        template:
          '<settings-page-header title="Content Curation Guidelines"></settings-page-header>',
      },
      'settings-content': {
        template: componentNameToTemplate('contentCurationGuidelines'),
      },
    },
    data: {
      title: 'Content Curation Guidelines',
      navGroup: NavGroup.HOME,
      hasAccess: () =>
        hasAllFeatures(
          // TODO: PD-328293 - Clean up the feature flag ai_content_moderation_guidelines
          FeatureNames.AiContentModerationGuidelines,
          FeatureNames.AiContentModeration,
        ),
    },
  }),
};
